/**
 * cart-drawer.js — Dawn-compatible cart drawer.
 *
 * Registers the same custom elements Shrine uses (cart-drawer,
 * cart-drawer-items, cart-remove-button, quantity-input, cart-note) BEFORE
 * Shrine's CDN script loads so Shrine's definitions are silently rejected
 * (customElements.define throws on a name that is already registered).
 *
 * All cart behaviour lives here; Shrine's CDN JavaScript is never involved
 * in any cart operation.
 */
(function () {
  'use strict';

  if (window.__cartDrawerJsInstalled) return;
  window.__cartDrawerJsInstalled = true;

  // Capture fetch now, before Shrine's CDN main.js (deferred, loads after
  // this file) installs its own wrapper. Shrine's ShrFetch independently
  // watches window.fetch for cart mutations and dispatches its own
  // cart:open/cart:refresh on its own timing — anywhere from under a second
  // to 7+ seconds observed in testing — which our cart:open/cart:refresh
  // listeners below then treated as a fresh trigger, re-running the whole
  // sync+refresh pipeline (and re-flashing the loading spinner) for
  // mutations we'd already handled ourselves. Using this reference for our
  // own cart calls instead of the (later, Shrine-wrapped) global fetch makes
  // them invisible to ShrFetch, so it has nothing to react to — the same
  // technique cart-free-gifts.js already uses for its own gift-sync calls.
  // Prefer cart-free-gifts.js's already-captured reference (it loads first)
  // over capturing our own — both still land before Shrine either way.
  var _fetch = window.__originalFetch || window.fetch.bind(window);

  // ── Helpers ────────────────────────────────────────────────────────

  function debounce(fn, delay) {
    var t;
    return function () {
      var args = arguments;
      var ctx = this;
      clearTimeout(t);
      t = setTimeout(function () { fn.apply(ctx, args); }, delay);
    };
  }

  function cartRoot() {
    var r = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    return r.slice(-1) === '/' ? r : r + '/';
  }

  function postJSON(path, body) {
    return _fetch(cartRoot() + path, {
      method: 'POST',
      credentials: 'same-origin',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify(body),
    }).then(function (res) {
      if (!res.ok) throw new Error(path + ' ' + res.status);
      return res.json();
    });
  }

  // ── DOM update helpers ─────────────────────────────────────────────

  function parseSection(html) {
    return new DOMParser().parseFromString(html, 'text/html');
  }

  function applyCartSectionHtml(html) {
    var doc = parseSection(html);

    // Update drawer inner content
    // Dawn structure: cart-drawer > #CartDrawer > .drawer__inner
    var newInner = doc.querySelector('.drawer__inner');
    var curInner = document.querySelector('.drawer__inner');
    if (newInner && curInner) curInner.innerHTML = newInner.innerHTML;

    // Sync is-empty class on the cart-drawer element (Dawn: it lives on <cart-drawer>, not #CartDrawer)
    var newDrawer = doc.querySelector('cart-drawer');
    var curDrawer = document.querySelector('cart-drawer');
    if (newDrawer && curDrawer) {
      curDrawer.classList.toggle('is-empty', newDrawer.classList.contains('is-empty'));
    }

    initCountdowns();
    initTncCheckboxes();
    initCartDrawerCustom();
    fillEmptyCartImages();
  }

  // Empty-state cards use data-src so they stay off the first network queue.
  // Section refresh replaces .drawer__inner via innerHTML, which does not
  // re-run the inline filler. Load them once the drawer is actually open,
  // including when the last item is removed while it is already open.
  function fillEmptyCartImages() {
    var drawer = document.querySelector('cart-drawer');
    if (!drawer || !drawer.classList.contains('active')) return;
    drawer.querySelectorAll('.cart-v2-empty img[data-src]').forEach(function (img) {
      if (!img.getAttribute('src')) img.src = img.getAttribute('data-src');
    });
  }

  function applyBubbleHtml(html) {
    var curBubble = document.getElementById('cart-icon-bubble');
    if (!curBubble) return;
    // The cart-icon-bubble section renders the inner content only (no wrapper with
    // id="cart-icon-bubble"), so set the current bubble's innerHTML directly.
    var doc = parseSection(html);
    curBubble.innerHTML = doc.body ? doc.body.innerHTML : html;
  }

  // /cart page — sections/main-cart-items.liquid (section id "cart-items" in
  // templates/cart.json). Only relevant when that page is actually on screen
  // (see the getElementById guard in refreshSections), e.g. so a gift-only
  // cart just cleared by cart-free-gifts.js shows its empty state here too,
  // not just in the drawer.
  function applyCartPageItemsHtml(html) {
    var doc = parseSection(html);
    var newRoot = doc.querySelector('cart-items');
    var curRoot = document.querySelector('cart-items');
    if (!newRoot || !curRoot) return;
    curRoot.innerHTML = newRoot.innerHTML;
    curRoot.classList.toggle('is-empty', newRoot.classList.contains('is-empty'));
  }

  function applyCartPageFooterHtml(html) {
    var doc = parseSection(html);
    var newFooter = doc.getElementById('main-cart-footer');
    var curFooter = document.getElementById('main-cart-footer');
    if (!newFooter || !curFooter) return;
    curFooter.innerHTML = newFooter.innerHTML;
    curFooter.classList.toggle('is-empty', newFooter.classList.contains('is-empty'));
  }

  function applySections(sections) {
    if (!sections) return;
    if (sections['cart-drawer']) applyCartSectionHtml(sections['cart-drawer']);
    if (sections['cart-icon-bubble']) applyBubbleHtml(sections['cart-icon-bubble']);
    if (sections['cart-items']) applyCartPageItemsHtml(sections['cart-items']);
    if (sections['cart-footer']) applyCartPageFooterHtml(sections['cart-footer']);
    // Section HTML can re-enable Checkout; keep it locked while a mutation,
    // gift sync, or checkout-prep is still in flight.
    if (isCartUiBusy()) setCheckoutButtonsDisabled(true);
  }

  // Instant empty cart chrome — used on Back from checkout so the shopper
  // does not wait for /cart/clear.js + section re-render. Hides the header
  // count and item list immediately; applySections() later fills in the
  // proper empty-state markup once the server cart is actually empty.
  function paintEmptyCartUi() {
    document.documentElement.classList.add('cart-clearing-on-back');

    var drawer = document.querySelector('cart-drawer');
    if (drawer) {
      drawer.classList.add('is-empty');
      drawer.classList.remove('active');
      drawer.setAttribute('aria-hidden', 'true');
    }

    var itemsEl = document.querySelector('cart-drawer-items');
    if (itemsEl) itemsEl.classList.add('is-empty');

    var contents = document.getElementById('CartDrawer-CartItems');
    if (contents) contents.innerHTML = '';

    var titleCount = document.querySelector('.cart-v2-header__title-count');
    if (titleCount) {
      titleCount.textContent = '(0) Items';
    } else {
      var title = document.querySelector('.cart-v2-header__title');
      if (title) {
        title.innerHTML =
          '<span class="cart-v2-header__title-main">Cart</span>' +
          '<span class="cart-v2-header__title-count">(0) Items</span>';
      }
    }

    var bubbles = document.querySelectorAll('.cart-count-bubble');
    for (var i = 0; i < bubbles.length; i++) bubbles[i].remove();

    document.body.classList.remove('overflow-hidden');
  }

  // ── Refresh sequencing ──────────────────────────────────────────────
  // Multiple independent triggers (Shrine's own cart:open event, our fetch
  // watcher, quantity/remove actions) can all react to the same cart mutation.
  // A monotonic token makes any response that's been superseded by a newer
  // trigger a no-op instead of letting an out-of-order network response
  // overwrite the DOM with stale cart state.
  var refreshToken = 0;
  function beginRefresh() { return ++refreshToken; }
  function isStale(token) { return token !== refreshToken; }

  // Full re-fetch of cart sections (used when we don't already have section HTML)
  function refreshSections(token) {
    if (token === undefined) token = beginRefresh();
    var wasOpen = getDrawerEl() && getDrawerEl().classList.contains('active');
    // #main-cart-items only exists on the /cart page (sections/main-cart-items.liquid) —
    // pull its own sections too so mutations driven from elsewhere (drawer, gift
    // sync) stay in sync with that page's table/footer, not just the drawer.
    var sectionIds = 'cart-drawer,cart-icon-bubble';
    if (document.getElementById('main-cart-items')) sectionIds += ',cart-items,cart-footer';
    return _fetch(cartRoot() + '?sections=' + sectionIds, {
      credentials: 'same-origin',
      headers: { Accept: 'application/json' },
    })
      .then(function (res) { return res.json(); })
      .then(function (data) {
        if (isStale(token)) return;
        applySections(data);
        // Don't auto-reopen during bfcache restore — the pageshow handler
        // controls that explicitly after the full restore sequence completes.
        // viewOnly: the drawer was already open before this refresh (e.g. an
        // upsell "+Add" click while the drawer is showing) — this is just
        // reapplying visibility after applySections() replaced its markup,
        // not a fresh add-to-cart decision, so it must never redirect to
        // checkout even on a page/product configured to force one.
        if (wasOpen && !window.__cartBfcacheRestoring) openDrawer(false, true);
      });
  }

  var cartUiBusy = false;
  var cartUiBusyCount = 0;

  function setCheckoutButtonsDisabled(on) {
    document.querySelectorAll('button[name="checkout"], input[name="checkout"]').forEach(function (btn) {
      if (on) {
        btn.setAttribute('aria-busy', 'true');
        if (!btn.hasAttribute('data-busy-prev-disabled')) {
          btn.setAttribute('data-busy-prev-disabled', btn.disabled ? '1' : '0');
        }
        btn.disabled = true;
        return;
      }
      var was = btn.getAttribute('data-busy-prev-disabled');
      btn.removeAttribute('aria-busy');
      btn.removeAttribute('data-busy-prev-disabled');
      if (was === '0') btn.disabled = false;
    });
  }

  function isCartUiBusy() {
    return cartUiBusyCount > 0 || !!window.__larineCheckoutSyncing || !!window.__cartFreeGiftsSyncing;
  }

  function applyCartBusyUi() {
    cartUiBusy = isCartUiBusy();
    var el = document.querySelector('cart-drawer-items');
    if (el) el.classList.toggle('is-loading', cartUiBusy);
    // Never re-enable Checkout while gifts are still being removed after a
    // paid-line delete — applySections() from change.js would otherwise paint
    // a live button over leftover $0 gift lines.
    setCheckoutButtonsDisabled(cartUiBusy);
  }

  function setCartItemsLoading(on) {
    if (on) {
      cartUiBusyCount += 1;
    } else {
      cartUiBusyCount -= 1;
      if (cartUiBusyCount < 0) cartUiBusyCount = 0;
    }
    applyCartBusyUi();
  }

  function resetCheckoutBusyState() {
    window.__larineCheckoutSyncing = false;
    cartUiBusyCount = 0;
    applyCartBusyUi();
  }

  // Single-flight pipeline: every trigger that wants to sync gifts, refresh
  // the drawer markup, and (optionally) open the drawer funnels through here
  // so the work is serialized (never overlapping) instead of racing. Every
  // caller gets the same loading indicator for free instead of each flow
  // needing its own — a superseded (stale) run's own "turn loading off" is
  // skipped so it can't clear the spinner out from under a newer run.
  var pipelineChain = Promise.resolve();
  var lastPipelineFinishedAt = 0;
  function runPipeline(wantOpen, cartData, opts) {
    if (window.__cartClearingOnBack) return pipelineChain;
    var token = beginRefresh();
    var ownsLoading = !(opts && opts.keepLoading);
    if (ownsLoading) setCartItemsLoading(true);
    pipelineChain = pipelineChain
      .then(function () { return syncLaborGifts(cartData); })
      .then(function (laborResult) {
        return syncFreeGifts(laborResult && laborResult.changed ? undefined : cartData);
      })
      .then(function () { return refreshSections(token); })
      .then(function () {
        if (wantOpen && !isStale(token)) openDrawer();
      })
      .catch(function () {})
      .then(function () {
        if (ownsLoading) setCartItemsLoading(false);
        if (!isStale(token)) lastPipelineFinishedAt = Date.now();
      });
    return pipelineChain;
  }

  // Shrine's own fetch wrapper reacts to the same mutations we do, but
  // independently and on its own timing — its cart:open/cart:refresh dispatch
  // for a mutation we already handled ourselves can land a second or so after
  // our own runPipeline() already finished, re-flashing the loading spinner
  // for a refresh that's redundant (we're already showing current state).
  // Direct runPipeline() calls from our own code (quantity change, trash
  // button, upsell add) are untouched by this — only the event-triggered
  // echoes below are suppressed, and only briefly.
  var PIPELINE_ECHO_COOLDOWN_MS = 2000;
  function isPipelineEchoWindow() {
    return Date.now() - lastPipelineFinishedAt < PIPELINE_ECHO_COOLDOWN_MS;
  }

  // ── Drawer open / close ────────────────────────────────────────────

  function getDrawerEl() {
    // Dawn structure: <cart-drawer class="drawer"> is the outer animated container.
    // #CartDrawer is the inner div; active/is-empty classes live on cart-drawer element.
    return document.querySelector('cart-drawer');
  }

  /**
   * @returns {string} root-relative checkout path + search
   */
  function getStoreCheckoutUrl() {
    if (typeof window.__larineCheckoutDiscountUrl === 'string' && window.__larineCheckoutDiscountUrl) {
      return window.__larineCheckoutDiscountUrl;
    }
    var raw = window.routes && window.routes.cart_url;
    if (typeof raw !== 'string' || !raw.length) return '/checkout';
    try {
      var u = new URL(raw, window.location.origin);
      u.pathname = u.pathname.replace(/\/cart\/?$/, '/checkout');
      return u.pathname + u.search;
    } catch (e) {
      return '/checkout';
    }
  }

  function openDrawer(animate, viewOnly) {
    // Site-wide kill switch (Theme settings > Cart > "Disable cart drawer"),
    // a narrower override for Shopify pages (Theme settings > Cart >
    // "Redirect to checkout on landing pages") — e.g. Replo landing pages at
    // /pages/*, and a per-product override (Product > Metafields > "Force
    // checkout redirect") for individual products that should always send
    // shoppers straight to checkout after adding to cart, regardless of the
    // site-wide drawer setting above. Every trigger funnels through this one
    // function (the public cartDrawerCustom.open() API, the CartDrawer
    // custom element's own .open() used by the header cart icon, and
    // runPipeline's auto-open for any detected cart mutation), so gating
    // here covers all of them without needing to touch each call site
    // individually — except `viewOnly`, set by callers that are restoring or
    // maintaining a drawer that's already open/was already open (explicit
    // header cart icon / any /cart link click; refreshSections() reapplying
    // visibility after a section refresh while the drawer was already
    // showing, e.g. an upsell "+Add" click inside it; bfcache-restore
    // reopen; closing the cart-drawer upsell's flavors modal, whether or not
    // it ended in an add). None of those are a fresh add-to-cart decision —
    // they're the shopper already in the drawer or asking to see it — so
    // they shouldn't get swept into a checkout redirect just because this
    // /pages/* URL or product forces one on a genuine add-to-cart. The
    // site-wide "Disable cart drawer" toggle is the one exception that still
    // applies even to viewOnly — when it's on there effectively is no drawer
    // to view, by that setting's own design (see its theme-setting info text).
    var onPage = window.location.pathname.indexOf('/pages/') !== -1;
    if (
      window.__cartDrawerDisabled ||
      (!viewOnly &&
        ((window.__cartDrawerForceCheckoutOnPages && onPage) ||
          window.__cartDrawerForceCheckoutOnProduct))
    ) {
      window.location.href = getStoreCheckoutUrl();
      return;
    }

    var el = getDrawerEl();
    if (!el) return;

    // Clear any stale overlay classes that other scripts (Shrine, bundle modal) may have left
    document.body.classList.remove(
      'nw-new-bundle-flavors-modal-open',
      'nw-new-bundle-checkout-upsell-open'
    );
    var upsellModal = document.getElementById('nw-new-bundle-checkout-upsell-modal');
    if (upsellModal) {
      upsellModal.classList.remove('is-open');
      upsellModal.setAttribute('aria-hidden', 'true');
    }
    var flavorsModal = document.getElementById('nw-new-bundle-flavors-modal');
    if (flavorsModal) {
      flavorsModal.classList.remove('is-open');
      flavorsModal.setAttribute('aria-hidden', 'true');
    }

    el.classList.add('active');
    el.removeAttribute('aria-hidden');
    document.body.classList.add('overflow-hidden');
    fillEmptyCartImages();
    var inner = el.querySelector('.drawer__inner');
    if (inner) {
      // Force the transform via inline style so Shrine's own CSS rules
      // (which may use higher-specificity selectors like #CartDrawer) cannot
      // keep the panel off-screen.
      inner.style.setProperty('transform', 'translateX(0)', 'important');
      if (animate !== false) inner.focus();
    }
  }

  function closeDrawer() {
    var el = getDrawerEl();
    if (!el) return;
    var inner = el.querySelector('.drawer__inner');
    if (inner) {
      // Remove the inline style so CSS translateX(100%) takes over.
      // Setting translateX(100%) !important inline would lock the panel
      // off-screen even when CSS .drawer.active .drawer__inner tries to
      // re-open it, because inline !important beats author-stylesheet !important.
      inner.style.removeProperty('transform');
    }
    el.classList.remove('active');
    el.setAttribute('aria-hidden', 'true');
    document.body.classList.remove('overflow-hidden');
  }

  // ── Cart drawer v2 interactions ───────────────────────────────────

  function initCartDrawerCustom() {
    // Flavors accordion toggle
    document.querySelectorAll('[data-flavors-toggle]').forEach(function (btn) {
      if (btn.__flavorsInit) return;
      btn.__flavorsInit = true;
      btn.addEventListener('click', function () {
        var expanded = btn.getAttribute('aria-expanded') === 'true';
        btn.setAttribute('aria-expanded', expanded ? 'false' : 'true');
        var flavorsEl = btn.closest('.cart-item-v2__flavors');
        var body = flavorsEl && flavorsEl.querySelector('[data-flavors-body]');
        if (body) body.classList.toggle('is-open', !expanded);
      });
    });

    // Upsell carousel
    document.querySelectorAll('.cart-v2-upsell').forEach(function (upsellEl) {
      if (upsellEl.__upsellInit) return;
      upsellEl.__upsellInit = true;

      var track = upsellEl.querySelector('.cart-v2-upsell__track');
      var prevBtn = upsellEl.querySelector('[data-upsell-prev]');
      var nextBtn = upsellEl.querySelector('[data-upsell-next]');
      if (!track) return;

      var cards = Array.from(track.children);
      var current = 0;
      var navEl = upsellEl.querySelector('.cart-v2-upsell__nav');
      if (navEl) navEl.style.display = cards.length <= 1 ? 'none' : '';

      function updateNav() {
        if (prevBtn) prevBtn.disabled = current === 0;
        if (nextBtn) nextBtn.disabled = current >= cards.length - 1;
      }

      function goTo(idx) {
        current = Math.max(0, Math.min(cards.length - 1, idx));
        var card = cards[0];
        var cardW = card ? card.offsetWidth : 0;
        track.style.transform = 'translateX(-' + (current * (cardW + 12)) + 'px)';
        updateNav();
      }

      if (prevBtn) prevBtn.addEventListener('click', function () { goTo(current - 1); });
      if (nextBtn) nextBtn.addEventListener('click', function () { goTo(current + 1); });

      function syncUpsellSubscribe(card) {
        if (!card) return;
        var checkbox = card.querySelector('[data-upsell-subscribe]');
        if (!checkbox) return;
        var subscribed = checkbox.checked;
        var priceEl = card.querySelector('[data-upsell-price]');
        var compareEl = card.querySelector('[data-upsell-compare]');
        var addBtn = card.querySelector('[data-upsell-add]');
        var price = subscribed ? card.getAttribute('data-price-sub') : card.getAttribute('data-price-otp');
        var compare = subscribed ? card.getAttribute('data-compare-sub') : card.getAttribute('data-compare-otp');
        if (priceEl && price) priceEl.textContent = price;
        if (compareEl) {
          if (compare) {
            compareEl.textContent = compare;
            compareEl.removeAttribute('hidden');
          } else {
            compareEl.setAttribute('hidden', '');
          }
        }
        if (addBtn) {
          var planId = card.getAttribute('data-selling-plan-id');
          if (subscribed && planId) addBtn.setAttribute('data-selling-plan-id', planId);
          else addBtn.removeAttribute('data-selling-plan-id');
        }
      }

      upsellEl.querySelectorAll('[data-upsell-subscribe]').forEach(function (checkbox) {
        if (checkbox.__upsellSubscribeInit) return;
        checkbox.__upsellSubscribeInit = true;
        checkbox.addEventListener('change', function () {
          syncUpsellSubscribe(checkbox.closest('.cart-v2-upsell__card'));
        });
        syncUpsellSubscribe(checkbox.closest('.cart-v2-upsell__card'));
      });

      // "+" add to cart buttons
      upsellEl.querySelectorAll('[data-upsell-add]').forEach(function (addBtn) {
        if (addBtn.__upsellAddInit) return;
        addBtn.__upsellAddInit = true;
        addBtn.addEventListener('click', function () {
          var vid = addBtn.dataset.upsellAdd;
          if (!vid || addBtn.classList.contains('is-loading')) return;
          syncUpsellSubscribe(addBtn.closest('.cart-v2-upsell__card'));
          var origText = addBtn.textContent;
          addBtn.classList.add('is-loading');
          addBtn.textContent = 'Adding…';
          // Without this, the global fetch interceptor also sees this same
          // cart/add.js call and fires its own runPipeline(true) — doubling
          // every sync + section refresh on top of the explicit call below.
          window.__cartDrawerUpdating = true;
          var payload = { id: Number(vid), quantity: 1 };
          var sellingPlanId = parseInt(addBtn.dataset.sellingPlanId, 10);
          if (sellingPlanId > 0) payload.selling_plan = sellingPlanId;
          postJSON('cart/add.js', payload)
            .then(function () {
              addBtn.classList.remove('is-loading');
              addBtn.classList.add('is-added');
              addBtn.textContent = '✓ Added';
              return runPipeline(false);
            })
            .catch(function () {
              addBtn.classList.remove('is-loading');
              addBtn.textContent = origText;
            })
            .then(function () {
              window.__cartDrawerUpdating = false;
            });
        });
      });

      updateNav();
    });

    // "+" buttons that open the (sitewide) flavor picker instead of adding
    // directly — Gum-type upsell products.
    document.querySelectorAll('[data-upsell-open-flavors]').forEach(function (btn) {
      if (btn.__upsellFlavorsInit) return;
      btn.__upsellFlavorsInit = true;
      btn.addEventListener('click', openUpsellFlavorsModal);
    });

    // Trash button — removes all lines in a product+purchase-type group.
    // One cart/update.js with every line key → 0, not a chain of cart/change.js.
    // Javvy flavor groups are several lines (primed 2+2+2+2); after the first
    // change.js, cart-free-gifts remaps the rest onto a new selling plan and
    // those keys die, so a sequential remove only ever took −2. Keys (not
    // 1-based indexes) still disambiguate the same variant on two plans.
    document.querySelectorAll('[data-remove-product]').forEach(function (btn) {
      if (btn.__removeProductInit) return;
      btn.__removeProductInit = true;
      btn.addEventListener('click', function () {
        var productId = btn.dataset.removeProduct;
        var group = btn.closest('.cart-item-v2') ||
          document.querySelector('.cart-item-v2[data-product-id="' + productId + '"]');
        if (!group) return;
        var updates = {};
        Array.from(group.querySelectorAll('.quantity__input[data-line-key]')).forEach(function (input) {
          var key = input.dataset.lineKey;
          if (key) updates[key] = 0;
        });
        if (!Object.keys(updates).length) return;
        window.__cartDrawerUpdating = true;
        setCartItemsLoading(true);
        postJSON('cart/update.js', { updates: updates })
          .then(function (cart) { return runPipeline(false, cart, { keepLoading: true }); })
          .catch(function (e) { console.error('[cart-drawer] remove product group:', e); })
          .then(function () {
            window.__cartDrawerUpdating = false;
            setCartItemsLoading(false);
          });
      });
    });
  }

  // ── Upsell flavor picker ─────────────────────────────────────────────
  // sections/nw-new-bundle-flavors-global.liquid renders one sitewide
  // instance of #nw-new-bundle-flavors-modal (theme.liquid), so there's
  // nothing to fetch or lazy-load here — just close the drawer (so the two
  // overlays don't show at once), mark the modal so it renders as a
  // same-width right-side panel instead of its native centered PDP dialog
  // (see .is-cart-drawer-context CSS), and open it.

  function openUpsellFlavorsModal() {
    var modal = document.getElementById('nw-new-bundle-flavors-modal');
    if (!modal || !window.NwNewBundle || typeof window.NwNewBundle.openFlavorsModal !== 'function') {
      console.error('[cart-drawer] flavors picker unavailable — check sections/nw-new-bundle-flavors-global.liquid settings');
      return;
    }
    if (window.cartDrawerCustom && typeof window.cartDrawerCustom.close === 'function') {
      window.cartDrawerCustom.close();
    }
    modal.classList.add('is-cart-drawer-context');

    // Mirror the drawer's own (already-live, section-refreshed) item count
    // instead of re-deriving it, so this never goes stale independently.
    var headingTitle = modal.querySelector('[data-nw-cart-heading-title]');
    var drawerTitle = document.querySelector('.cart-v2-header__title');
    if (headingTitle && drawerTitle) headingTitle.textContent = drawerTitle.textContent;

    window.__nwUpsellFlavorsFlow = true;
    window.NwNewBundle.openFlavorsModal();
  }

  // ── Countdown timer ────────────────────────────────────────────────

  function initCountdowns() {
    document.querySelectorAll('[data-countdown]').forEach(function (el) {
      if (el.__cdRunning) return;
      el.__cdRunning = true;
      var raw = (el.dataset.duration || '').trim();
      var secs = 0;
      if (/^\d+$/.test(raw)) {
        secs = parseInt(raw, 10);
      } else {
        var parts = raw.split(':').map(Number);
        if (parts.length === 3) secs = parts[0] * 3600 + parts[1] * 60 + parts[2];
        else if (parts.length === 2) secs = parts[0] * 60 + parts[1];
      }
      function pad(n) { return String(n).padStart(2, '0'); }
      function render() {
        var h = Math.floor(secs / 3600);
        var m = Math.floor((secs % 3600) / 60);
        var s = secs % 60;
        el.textContent = h > 0 ? pad(h) + ':' + pad(m) + ':' + pad(s) : pad(m) + ':' + pad(s);
      }
      render();
      var tick = setInterval(function () {
        if (secs <= 0) { clearInterval(tick); return; }
        secs--;
        render();
      }, 1000);
    });
  }

  // ── TnC checkbox ───────────────────────────────────────────────────

  function initTncCheckboxes() {
    document.querySelectorAll('[data-tnc-checkbox]').forEach(function (el) {
      if (el.__tncInit) return;
      el.__tncInit = true;
      el.style.cursor = 'pointer';

      el.addEventListener('click', function () {
        var checked = el.dataset.checked !== 'true';
        el.dataset.checked = checked ? 'true' : 'false';

        if (el.dataset.disableButton === 'true') {
          [document.getElementById('CartDrawer-Checkout'),
           document.getElementById('CartDrawer-Checkout_popup')]
            .forEach(function (btn) {
              if (!btn) return;
              btn.disabled = !checked;
              btn.setAttribute('aria-disabled', checked ? 'false' : 'true');
            });
        }
        document.querySelectorAll('.tnc-checkbox-warning').forEach(function (w) {
          if (checked) w.classList.add('hidden');
        });
      });

      [document.getElementById('CartDrawer-Checkout'),
       document.getElementById('CartDrawer-Checkout_popup')]
        .forEach(function (btn) {
          if (!btn) return;
          btn.addEventListener('click', function (e) {
            if (el.dataset.checked === 'true') return;
            e.preventDefault();
            e.stopImmediatePropagation();
            var pos = (el.dataset.warningPosition || 'under_button').replace(/_/g, '-');
            var text = el.dataset.warningText || '';
            document.querySelectorAll('.tnc-checkbox-warning').forEach(function (w) {
              var show = w.classList.contains('tnc-checkbox-warning--' + pos);
              w.classList.toggle('hidden', !show);
              if (show && text) w.textContent = text;
            });
          }, true);
        });
    });
  }

  // ── Cart-drawer flavor-group minimum ────────────────────────────────
  // A multi-flavor bundle card's total box count can never drop below this
  // via its own per-flavor quantity steppers — mirrors the same floor
  // enforced on the PDP/upsell flavors picker (handleFlavorMinus in
  // nw-new-bundle.js), just applied to an already-in-cart line group
  // instead of a fresh pick. Liquid renders the steady-state disabled
  // button (see snippets/cart-drawer.liquid); this is the runtime backstop
  // for rapid clicks landing between cart syncs.

  var FLAVOR_ROW_GROUP_MIN_BOXES = 2;

  // Only .cart-flavor-row quantity inputs (the per-flavor accordion rows)
  // are part of a group floor — the flavorless single-item-card stepper
  // (toothpaste, mints) reuses the same <quantity-input> element but isn't
  // wrapped in .cart-flavor-row, so it's excluded automatically.
  function getFlavorRowGroupInputs(input) {
    var row = input.closest('.cart-flavor-row');
    if (!row) return null;
    var body = row.closest('[data-flavors-body]');
    if (!body) return null;
    return Array.prototype.slice.call(body.querySelectorAll('.cart-flavor-row__qty .quantity__input'));
  }

  // Lowest this specific row can go while keeping the group's total at or
  // above the floor, given every OTHER row's current value.
  function getFlavorRowMinAllowedQty(input) {
    var siblings = getFlavorRowGroupInputs(input);
    if (!siblings) return 0;
    var othersTotal = 0;
    siblings.forEach(function (el) {
      if (el === input) return;
      othersTotal += parseInt(el.value, 10) || 0;
    });
    return Math.max(0, FLAVOR_ROW_GROUP_MIN_BOXES - othersTotal);
  }

  // ── QuantityInput custom element ───────────────────────────────────

  var QuantityInput = (function () {
    function QuantityInput() {
      return Reflect.construct(HTMLElement, [], QuantityInput);
    }
    QuantityInput.prototype = Object.create(HTMLElement.prototype);
    QuantityInput.prototype.constructor = QuantityInput;

    QuantityInput.prototype.connectedCallback = function () {
      this.input = this.querySelector('.quantity__input');
      if (!this.input) return;
      this.changeEvent = new Event('change', { bubbles: true });
      var self = this;
      this.querySelectorAll('.quantity__button').forEach(function (btn) {
        btn.addEventListener('click', function (e) { self.onButtonClick(e); });
      });
    };

    QuantityInput.prototype.onButtonClick = function (event) {
      event.preventDefault();
      var btn = event.currentTarget;
      var prev = this.input.value;
      if (btn.name === 'plus') {
        this.input.stepUp();
      } else {
        var minAllowed = getFlavorRowMinAllowedQty(this.input);
        if (parseInt(this.input.value, 10) - 1 < minAllowed) return;
        this.input.stepDown();
      }
      if (prev !== this.input.value) this.input.dispatchEvent(this.changeEvent);
    };

    return QuantityInput;
  })();

  // ── CartRemoveButton custom element ────────────────────────────────

  var CartRemoveButton = (function () {
    function CartRemoveButton() {
      return Reflect.construct(HTMLElement, [], CartRemoveButton);
    }
    CartRemoveButton.prototype = Object.create(HTMLElement.prototype);
    CartRemoveButton.prototype.constructor = CartRemoveButton;

    CartRemoveButton.prototype.connectedCallback = function () {
      var self = this;
      if (self.__handler) return;
      self.__handler = function (event) {
        event.preventDefault();
        var items = self.closest('cart-drawer-items');
        if (items) items.updateQuantity(parseInt(self.dataset.index, 10), 0);
      };
      self.addEventListener('click', self.__handler);
    };

    CartRemoveButton.prototype.disconnectedCallback = function () {
      if (this.__handler) this.removeEventListener('click', this.__handler);
    };

    return CartRemoveButton;
  })();

  // ── CartNote custom element ────────────────────────────────────────

  var CartNote = (function () {
    function CartNote() {
      return Reflect.construct(HTMLElement, [], CartNote);
    }
    CartNote.prototype = Object.create(HTMLElement.prototype);
    CartNote.prototype.constructor = CartNote;

    CartNote.prototype.connectedCallback = function () {
      if (this.__noteHandler) return;
      var self = this;
      self.__noteHandler = debounce(function (event) {
        _fetch(cartRoot() + 'cart/update.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: event.target.value }),
        }).catch(function () {});
      }, 500);
      self.addEventListener('change', self.__noteHandler);
    };

    CartNote.prototype.disconnectedCallback = function () {
      if (this.__noteHandler) this.removeEventListener('change', this.__noteHandler);
    };

    return CartNote;
  })();

  // ── CartDrawerItems custom element ─────────────────────────────────

  var CartDrawerItems = (function () {
    function CartDrawerItems() {
      return Reflect.construct(HTMLElement, [], CartDrawerItems);
    }
    CartDrawerItems.prototype = Object.create(HTMLElement.prototype);
    CartDrawerItems.prototype.constructor = CartDrawerItems;

    CartDrawerItems.prototype.connectedCallback = function () {
      if (this.__changeHandler) return;
      var self = this;
      self.__changeHandler = debounce(function (event) {
        var input = event.target;
        if (!input || !input.classList.contains('quantity__input')) return;
        var qty = parseInt(input.value, 10);
        var lineKey = input.dataset.lineKey;
        var index = parseInt(input.dataset.index, 10);
        if (isNaN(qty) || (!lineKey && isNaN(index))) return;
        // Backstop for manual typing / scroll-wheel / keyboard changes on
        // the number input, which bypass QuantityInput's own button-click
        // guard — clamp back up to the group floor rather than send an
        // under-floor quantity to the server.
        var minAllowed = getFlavorRowMinAllowedQty(input);
        if (qty < minAllowed) {
          qty = minAllowed;
          input.value = String(minAllowed);
        }
        self.updateQuantity(lineKey || index, qty);
      }, 300);
      self.addEventListener('change', self.__changeHandler);
    };

    CartDrawerItems.prototype.disconnectedCallback = function () {
      if (this.__changeHandler) this.removeEventListener('change', this.__changeHandler);
    };

    CartDrawerItems.prototype.setLineLoading = function (line, on) {
      var item = document.getElementById('CartDrawer-Item-' + line);
      if (!item) return;
      var overlay = item.querySelector('.loading-overlay');
      if (overlay) overlay.classList.toggle('hidden', !on);
      var cartItemsEl = document.getElementById('CartDrawer-CartItems');
      if (cartItemsEl) cartItemsEl.classList.toggle('cart__items--disabled', on);
    };

    CartDrawerItems.prototype.updateQuantity = function (line, quantity) {
      // Prevent the generic fetch interceptor from double-firing for this request.
      window.__cartDrawerUpdating = true;
      var token = beginRefresh();
      setCartItemsLoading(true);

      var payload = {
        quantity: quantity,
        sections: 'cart-drawer,cart-icon-bubble',
      };
      if (typeof line === 'string') payload.id = line;
      else payload.line = line;

      postJSON('cart/change.js', payload)
        .then(function (data) {
          return syncLaborGifts(data).then(function (laborResult) {
            var giftHint = laborResult && laborResult.changed ? undefined : data;
            return syncFreeGifts(giftHint).then(function (giftResult) {
              var giftsChanged = giftResult === true || (giftResult && giftResult.changed);
              if (giftsChanged) {
                if (giftResult && giftResult.sections) {
                  if (!isStale(token)) applySections(giftResult.sections);
                  return;
                }
                return refreshSections(token);
              }
              if (laborResult && laborResult.changed) {
                if (!laborResult.sections && !isStale(token)) return refreshSections(token);
                return;
              }
              // Qty-to-0 of the paid product: change.js HTML still has $0
              // gifts. Painting it re-enables checkout for 1–2s before the
              // gift-only clear (or a later refresh) catches up.
              var leftover = data && Array.isArray(data.items) ? data.items : [];
              if (leftover.length && leftover.every(function (item) {
                return parseInt(item.final_price, 10) === 0;
              })) {
                return refreshSections(token);
              }
              if (!isStale(token)) applySections(data.sections);
            });
          });
        })
        .catch(function (err) {
          console.error('[cart-drawer] updateQuantity:', err);
          var errorEl = document.getElementById('CartDrawer-CartErrors');
          if (errorEl) errorEl.textContent = 'Error updating cart. Please try again.';
        })
        .then(function () {
          window.__cartDrawerUpdating = false;
          setCartItemsLoading(false);
        });
    };

    return CartDrawerItems;
  })();

  // ── CartDrawer custom element ──────────────────────────────────────

  var CartDrawer = (function () {
    function CartDrawer() {
      return Reflect.construct(HTMLElement, [], CartDrawer);
    }
    CartDrawer.prototype = Object.create(HTMLElement.prototype);
    CartDrawer.prototype.constructor = CartDrawer;

    CartDrawer.prototype.connectedCallback = function () {
      var self = this;
      if (self.__cdInit) return;
      self.__cdInit = true;

      // Overlay click closes drawer
      var overlay = document.getElementById('CartDrawer-Overlay');
      if (overlay) overlay.addEventListener('click', function () { closeDrawer(); });

      // Delegate close buttons inside the drawer
      self.addEventListener('click', function (e) {
        if (e.target.closest('[data-cart-drawer-close], .drawer__close')) closeDrawer();
      });

      // Escape key
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && self.classList.contains('active')) closeDrawer();
      });
    };

    // Dawn-compatible public methods (called by theme.liquid CartDrawerObserver)
    CartDrawer.prototype.open = function (viewOnly) { openDrawer(undefined, viewOnly); };
    CartDrawer.prototype.close = function () { closeDrawer(); };

    return CartDrawer;
  })();

  // ── Register elements before Shrine can ───────────────────────────

  [
    ['quantity-input',     QuantityInput],
    ['cart-remove-button', CartRemoveButton],
    ['cart-note',          CartNote],
    ['cart-drawer-items',  CartDrawerItems],
    ['cart-drawer',        CartDrawer],
  ].forEach(function (pair) {
    if (!customElements.get(pair[0])) {
      try { customElements.define(pair[0], pair[1]); } catch (e) {
        console.warn('[cart-drawer] could not define', pair[0], e);
      }
    }
  });

  // ── Cart link / open trigger delegation ───────────────────────────
  // Use capture phase (true) so our handler fires before Shrine's bubble-phase
  // listener, which is registered earlier in the deferred-script order and
  // would otherwise show Shrine's own cart UI first.

  document.addEventListener('click', function (e) {
    var openEl = e.target.closest('[data-cart-drawer-open]');
    if (!openEl) {
      var a = e.target.closest('a[href]');
      if (a) {
        var href = a.getAttribute('href');
        if (href === '/cart' || (href && href.endsWith('/cart'))) openEl = a;
      }
    }
    if (openEl) {
      e.preventDefault();
      // stopPropagation prevents the event from reaching Shrine's bubble-phase
      // handler entirely (since we're in capture phase); stopImmediatePropagation
      // blocks any other capture-phase listeners registered before us.
      e.stopPropagation();
      e.stopImmediatePropagation();
      // viewOnly: this is the shopper explicitly asking to see their cart
      // (header icon, any /cart link) — always show the drawer, never
      // redirect to checkout, even on pages/products configured to force it.
      openDrawer(undefined, true);
    }
  }, true); // capture phase — fires before Shrine's bubble-phase handler

  // ── cart:refresh / cart:open events ───────────────────────────────
  // Guard against events fired by Shrine during bfcache restore. Shrine's own
  // fetch-wrapping ("ShrFetch") is still alive even though its cart-drawer
  // custom element registration is preempted, so it still dispatches these
  // events on cart mutations — funnel them through the same single-flight
  // pipeline as our own triggers instead of racing a separate chain.

  document.addEventListener('cart:refresh', function (e) {
    if (window.__cartBfcacheRestoring || window.__cartClearingOnBack) return;
    if (isPipelineEchoWindow()) return;
    var shouldOpen = e.detail && e.detail.open;
    runPipeline(shouldOpen);
  });

  document.addEventListener('cart:open', function () {
    if (window.__cartBfcacheRestoring || window.__cartClearingOnBack) return;
    if (isPipelineEchoWindow()) return;
    runPipeline(true);
  });

  // ── Auto-refresh on external cart mutations ────────────────────────
  // Patches window.fetch to watch for add/change/update calls not
  // initiated by CartDrawerItems (which already handles its own re-render).

  var CART_MUTATION_RE = /\/cart\/(add|change|update)(\.js)?(\?|$)/;
  var CART_ADD_RE      = /\/cart\/add(\.js)?(\?|$)/;

  function syncLaborGifts(cartHint) {
    if (!window.laborCtaDiscount || typeof window.laborCtaDiscount.sync !== 'function') {
      return Promise.resolve({ changed: false });
    }
    return window.laborCtaDiscount.sync(cartHint);
  }

  function syncFreeGifts(cartData) {
    return window.cartFreeGifts ? window.cartFreeGifts.sync(cartData) : Promise.resolve(false);
  }

  function tncBlocksCheckout() {
    var boxes = document.querySelectorAll('[data-tnc-checkbox]');
    for (var i = 0; i < boxes.length; i++) {
      var el = boxes[i];
      if (el.dataset.disableButton !== 'true') continue;
      if (el.dataset.checked !== 'true') return true;
    }
    return false;
  }

  function waitWhileCartBusy() {
    var started = Date.now();
    return new Promise(function (resolve) {
      function check() {
        var busy = window.__cartDrawerUpdating || window.__laborCtaDiscountUpdating || window.__cartFreeGiftsSyncing;
        if (busy && Date.now() - started < 8000) {
          setTimeout(check, 40);
          return;
        }
        resolve();
      }
      check();
    }).then(function () {
      return pipelineChain;
    });
  }

  function prepareCartForCheckout() {
    return waitWhileCartBusy()
      .then(function () { return syncLaborGifts(); })
      .then(function () { return syncFreeGifts(); })
      .catch(function (err) {
        console.error('[cart-drawer] checkout sync failed', err);
      });
  }

  function goToCheckoutAfterSync() {
    if (window.__larineCheckoutSyncing) return;
    window.__larineCheckoutSyncing = true;
    setCartItemsLoading(true);

    prepareCartForCheckout()
      .then(function () {
        window.location.href = getStoreCheckoutUrl();
      })
      .catch(function () {
        window.__larineCheckoutSyncing = false;
        setCartItemsLoading(false);
      });
  }

  document.addEventListener('click', function (e) {
    var btn = e.target.closest && e.target.closest('button[name="checkout"], input[name="checkout"]');
    if (!btn || btn.disabled) return;
    if (tncBlocksCheckout()) return;
    e.preventDefault();
    e.stopPropagation();
    if (window.__larineCheckoutSyncing) return;
    goToCheckoutAfterSync();
  }, true);

  document.addEventListener('submit', function (e) {
    var form = e.target;
    if (!form || (form.id !== 'CartDrawer-Form' && form.id !== 'cart')) return;
    var submitter = e.submitter;
    if (submitter && submitter.getAttribute('name') !== 'checkout') return;
    if (tncBlocksCheckout()) return;
    e.preventDefault();
    e.stopPropagation();
    if (window.__larineCheckoutSyncing) return;
    goToCheckoutAfterSync();
  }, true);

  (function patchFetch() {
    if (!window.fetch) return;
    var orig = window.fetch.bind(window);
    window.fetch = function () {
      var args = arguments;
      var url = typeof args[0] === 'string' ? args[0] : ((args[0] && args[0].url) || '');
      // Capture these at request time, not response time. A flow (e.g. the
      // flavors checkout's clear/add/discount calls) sets __cartDrawerUpdating
      // for its own duration then clears it in a finally block that runs as
      // soon as its own await chain resolves — but this wrapped fetch's own
      // .then() below only fires once its *response* arrives and its body is
      // parsed, which can straggle in after that finally already ran. Re-reading
      // the live flag at that point would then wrongly see it cleared and fire
      // a second, redundant runPipeline() for a call the flow already handled
      // itself — the flickering "spinner on/off/on" the loading indicator made
      // visible.
      var wasUpdating = window.__cartDrawerUpdating || window.__cartClearingOnBack || window.__laborCtaDiscountUpdating;
      var wasGiftSyncing = window.__cartFreeGiftsSyncing;
      var wasBfcacheRestoring = window.__cartBfcacheRestoring || window.__cartClearingOnBack;

      return orig.apply(window, args).then(function (res) {
        if (
          res.ok &&
          CART_MUTATION_RE.test(url) &&
          !wasBfcacheRestoring &&
          !wasUpdating &&
          !wasGiftSyncing &&
          !isPipelineEchoWindow() &&
          !document.getElementById('CartDrawer-CartItems')
            ?.classList.contains('cart__items--disabled')
        ) {
          var isAdd = CART_ADD_RE.test(url);
          res.clone().json()
            .then(function () {
              // Shrine's own cart:open handler (dispatched via its still-alive
              // ShrFetch wrapper) may also react to this same mutation — both
              // funnel through the shared pipeline, which serializes the work
              // and discards whichever response ends up superseded, instead of
              // one side silently dropping its own open/refresh.
              runPipeline(isAdd);
            })
            .catch(function () {});
        }
        return res;
      });
    };
  })();

  // ── Init dynamic parts on load ─────────────────────────────────────

  function boot() {
    initCountdowns();
    initTncCheckboxes();
    initCartDrawerCustom();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // ── bfcache restore: reset overlay state when navigating back ────────
  // When the user goes to checkout then presses Back, the browser restores
  // this page from bfcache with the drawer's 'active' class frozen in place.
  // That produces a black overlay with no visible panel (the CSS transition
  // state is stale). Fix: hard-close the drawer, refresh cart state from the
  // server, then re-open only if the cart still has items.
  window.addEventListener('pageshow', function (e) {
    if (!e.persisted) return;

    // Checkout click sets __larineCheckoutSyncing + a loading count, then
    // navigates away without clearing them. Safari/iOS restores that frozen
    // JS heap from bfcache, so Checkout stays disabled until we reset it.
    resetCheckoutBusyState();

    // Block cart-zero-clear schedule during the restore window to prevent it
    // from clearing a cart whose total may be momentarily $0 while syncGifts
    // runs (free gift items added before paid items are confirmed).
    window.__cartBfcacheRestoring = true;

    // 1. Close flavors modal via public API (its backdrop causes the grey overlay)
    if (window.NwNewBundle && typeof window.NwNewBundle.closeFlavorsModal === 'function') {
      window.NwNewBundle.closeFlavorsModal();
    }

    // 2. Close checkout upsell modal directly (not in public API)
    var upsellModal = document.getElementById('nw-new-bundle-checkout-upsell-modal');
    if (upsellModal) {
      upsellModal.classList.remove('is-open');
      upsellModal.setAttribute('aria-hidden', 'true');
      upsellModal.querySelectorAll('[data-upsell-panel]').forEach(function (el) {
        el.classList.add('hidden');
        el.hidden = true;
      });
    }

    // 3. Strip all scroll-lock / overlay body classes.
    document.body.classList.remove(
      'nw-new-bundle-flavors-modal-open',
      'nw-new-bundle-checkout-upsell-open'
    );

    // 4. Hard-close the drawer immediately to clear the stale bfcache overlay.
    //    The frozen 'active' class causes the black overlay-without-panel state.
    //    We note whether it was open so we can re-open it after the refresh.
    var drawerEl = getDrawerEl();
    var wasActive = drawerEl && drawerEl.classList.contains('active');
    if (drawerEl) {
      drawerEl.classList.remove('active');
      drawerEl.setAttribute('aria-hidden', 'true');
    }
    document.body.classList.remove('overflow-hidden');

    // 5. Refresh cart from server, then re-open drawer if it was open and
    //    the cart still has items. If the cart is now empty (e.g. checkout
    //    completed) the drawer stays closed and the user sees the clean page.
    //
    //    Labor metafield gifts are $0 lines. Shopify drops them on the way
    //    through checkout, so restore them before painting sections.
    //
    //    We do NOT call syncFreeGifts() here. The bfcache snapshot already
    //    has the correct gift state from before navigation. Running syncFreeGifts
    //    would call cart/add.js which Shrine intercepts and responds to with a
    //    cart:open event, causing the drawer to open on an empty/incomplete cart.
    //    Gift sync will happen on the next real cart mutation.
    //
    //    If flavors/bundle checkout asked us to empty the cart on Back,
    //    cart-clear-on-back-navigation.js owns this restore. Do not re-fetch
    //    sections here — that GET can race the clear and paint the old cart
    //    back in after /cart/clear.js already succeeded.
    var pendingClearOnBack = false;
    try {
      var flag = false;
      try { if (sessionStorage.getItem('larineBundleCheckoutRedirect') === '1') flag = true; } catch (e1) {}
      try { if (!flag && localStorage.getItem('larineBundleCheckoutRedirect') === '1') flag = true; } catch (e2) {}
      if (!flag) {
        var parts = document.cookie.split(';');
        for (var i = 0; i < parts.length; i++) {
          if (parts[i].trim() === 'larineBundleCheckoutRedirect=1') { flag = true; break; }
        }
      }
      pendingClearOnBack =
        typeof window.__larineIsGumIgHideChrome === 'function' &&
        window.__larineIsGumIgHideChrome() &&
        flag;
    } catch (err) {}
    if (pendingClearOnBack || (window.__larineIsGumIgHideChrome && window.__larineIsGumIgHideChrome() && window.__cartClearingOnBack)) {
      // Leave __cartBfcacheRestoring true until cart-clear-on-back finishes.
      // Unlocking here lets gift-sync / section GET race /cart/clear.js and
      // paint the pre-checkout cart back in.
      paintEmptyCartUi();
      return;
    }

    //    __cartBfcacheRestoring stays true until AFTER the re-open so any
    //    cart:open events fired by Shrine during this window are ignored.
    syncLaborGifts()
      .then(function () { return refreshSections(); })
      .then(function () {
        resetCheckoutBusyState();
        if (wasActive) {
          var d = getDrawerEl();
          if (d && !d.classList.contains('is-empty')) {
            // viewOnly: restoring the drawer to the state it was already in
            // before this bfcache navigation, not a fresh add-to-cart — must
            // never redirect to checkout instead.
            openDrawer(undefined, true);
          }
        }
        window.__cartBfcacheRestoring = false;
      })
      .catch(function () {
        resetCheckoutBusyState();
        window.__cartBfcacheRestoring = false;
      });
  });

  // ── Public API (used by cart-zero-clear.js and quick-add scripts) ──

  window.cartDrawerCustom = {
    // viewOnly (optional): true when this open request is restoring/keeping
    // the drawer visible rather than a fresh add-to-cart decision — see
    // openDrawer()'s own doc comment for what that skips.
    open:    function (viewOnly) { openDrawer(undefined, viewOnly); },
    close:   function () { closeDrawer(); },
    refresh: function () { return runPipeline(false); },
    applySections: function (sections) { applySections(sections); },
    paintEmpty: function () { paintEmptyCartUi(); },
  };
})();
